import React from 'react'
import { useAppDispatch, useAppStore } from 'reduxTyped'
import { l } from '@manychat/manyui'

import * as buttonActions from 'common/builder/actions/buttonActions'
import { IButton } from 'common/builder/buttons/buttonInterfaces'
import { ButtonType } from 'common/builder/constants/ButtonType'
import * as buttonSelectors from 'common/builder/selectors/builder/buttonSelectors'
import * as entitySelectors from 'common/builder/selectors/builder/entitySelectors'
import { getUser } from 'common/core/selectors/appSelectors'
import { FieldGroupId } from 'common/fields/entities/enums'
import { Field } from 'common/fields/entities/field'
import { DeprecatedFields } from 'common/fields/helpers'
import MCEditorContext from 'components/MCTextEditor/MCEditorContext'

import { parseValue } from './dataFormat/parse/utils/parseValue'
import { IMCEditorProps, ICreateButton } from './MCEditorContextInterfaces'
import { AllowedValues, IVariableData } from './types'

export const withMCEditorContextProvider = <
  R extends UnsafeAnyObject,
  P extends { value?: AllowedValues },
>(
  Consumer: React.ComponentType<P>,
) =>
  React.forwardRef<R, P & IMCEditorProps>(
    (
      {
        blockId,
        builderId,
        inverse,
        toolbarConfig,
        variablesConfig = {},
        withHyperlinks,
        ...consumerProps
      },
      ref,
    ) => {
      const { getState } = useAppStore()
      const dispatch = useAppDispatch()
      const builderIdString = builderId ?? ''
      const blockIsString = blockId ?? ''

      const getSmartLinkCaption = (id: string) => {
        return buttonSelectors.getButtonCaption(getState(), builderIdString, id, {
          smartLink: true,
        })
      }

      const getButtonById = (id: string) =>
        entitySelectors.getButtonById(getState(), builderIdString, id)

      const createButton: ICreateButton = (type, urlOrTargetId) => {
        let initial: Partial<IButton> = {}
        urlOrTargetId = urlOrTargetId.trim()
        if (type === ButtonType.URL) {
          initial = { url: urlOrTargetId }
        } else if (type === ButtonType.CONTENT) {
          const target = entitySelectors.getNodeById(getState(), builderIdString, urlOrTargetId)
          if (target) {
            initial = { targetId: urlOrTargetId }
          }
        }
        const button = dispatch<IButton>(
          buttonActions.createSmartLink(builderId, blockId, type, initial),
        )
        return button?.id
      }

      const cloneSmartLink = (buttonId: string) => {
        const item: IButton =
          (dispatch(
            buttonActions.cloneSmartLink(builderId, buttonId, blockId),
          ) as unknown as IButton) || null

        return item
      }

      const defaultGetFields = (): Field[] => {
        return []
      }
      const getFields = variablesConfig.getFields || defaultGetFields

      const getVariableCaption = (id: string) => {
        const deprecated = DeprecatedFields.find((deprecation) => deprecation.pattern.test(id))
        if (deprecated) {
          return deprecated.getCaption(id)
        }

        return (
          getFields().find((field) => field.id === id)?.caption ?? l.translate('No field selected')
        )
      }

      const getVariablePreview = ({ id }: IVariableData) => {
        const field = getFields().find((field) => field.id === id)
        if (field?.group === FieldGroupId.SYSTEM) {
          const key = id as keyof ReturnType<typeof getUser>
          return `${getUser(getState())[key] || ''}`
        }
        return null
      }

      return (
        <MCEditorContext.Provider
          value={{
            variables: {
              config: variablesConfig,
              getFields,
              getLabelById: getVariableCaption,
              getPreviewValue: variablesConfig.preview ? getVariablePreview : undefined,
            },
            smartLinks: {
              getLabelById: getSmartLinkCaption,
              getButtonById,
              createButton,
              cloneSmartLink,
            },
            toolbar: {
              mode: toolbarConfig?.mode,
              tools: toolbarConfig?.tools ?? [],
              limit: toolbarConfig?.limit ?? 0,
              countVariableLength: toolbarConfig?.countVariableLength,
              countEncodedSymbols: toolbarConfig?.countEncodedSymbols,
              withExtraToolbarSpacing: toolbarConfig?.withExtraToolbarSpacing,
              includeInstagramCategoryToEmojiPicker:
                toolbarConfig?.includeInstagramCategoryToEmojiPicker,
            },
            builderId: builderIdString,
            blockId: blockIsString,
            inverse,
            value: parseValue(consumerProps.value),
            withHyperlinks: Boolean(withHyperlinks),
            useContentEditable: Boolean(consumerProps.useContentEditable),
            readonly: Boolean(consumerProps.readonly),
          }}
        >
          <Consumer ref={ref} {...(consumerProps as P)} />
        </MCEditorContext.Provider>
      )
    },
  )
